import { ReactElement, useRef, useState } from "react";
import { message, Select } from "antd";
import NormalSpin from "../../../base/basicComponents/spin";
import FooterPagination from "./procedureListSections/footerPagination";
import TopFilter from "./procedureListSections/topFilter";
import SearchAndColumnSelect from "./procedureListSections/searchAndColumnSelect";
import ListTable from "../../commonComponents/listTable";
import ProcedureListService from "../../../service/procedureList";
import TableFilterMap, { TableFilterMapKeys } from "./procedureListSections/tableFilterMap";
import ProcedureListListColumn from "./procedureListSections/procedureListColumn";
import {
  allColumnsInProcedureList,
  defaultSelectedColumns,
} from "./procedureListSections/columnSelectOption";
import styles from "./index.module.scss";
import { useMount, useRequest, useSetState } from "ahooks";
import {
  sortUsersList,
  downloadFileByUrl,
  getTimeZoneString,
  getDate,
} from "../../publicDictionaryValues";
import AppSetting from "../../../config/AppSetting";
import DownloadDocument from "../../sections/downloadDocumentSection";
import ShareDocument from "../../sections/shareDocumentSection";
import generateCoverSheet, {
  downloadPDF,
} from "app/commonComponents/fileManagementFields/coverSheet";
import { FilterOptionList, SelectOptions } from "./procedureListSections/topFilter/types";
import {
  GetCoverSheetPayloadType,
  GetCoverSheetResponseType,
  GetProcedureListFilterNewResponseType,
  GetProcedureListNewPayloadType,
  GetProcedureListNewResponseType,
  SortItems,
} from "service/procedureList/types";
import {
  convertedTasks,
  createFakeProgressGenerator,
  getFilterResult,
  getInitTableFilterData,
  getInitTablePayloadData,
  handleUserRole,
} from "./utils";
import { FileListItem } from "./types";
import { AssignPPOSelectStatus } from "./procedureListSections/assignPPO";

const columnSelectValue = defaultSelectedColumns();
const columnSelectOption = allColumnsInProcedureList();
const getFakeProgress = createFakeProgressGenerator(98, 0.05);
let timer: NodeJS.Timeout | null = null;
type ColumnOption = (typeof columnSelectOption)[number];
type ColumnOptionKey = ColumnOption["value"];

type ColumnBooleanMap = {
  [K in ColumnOptionKey]: boolean;
};

export default function ProcedureList() {
  const selectRef = useRef<any>(null);
  const searchAndColumnSelectRef = useRef<any>(null);

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [procedureFiles, setProcedureFiles] = useState<FileListItem[]>([]);
  const [supportingFiles, setSupportingFiles] = useState<FileListItem[]>([]);
  const [idsParam, setIdsParam] = useState<GetCoverSheetPayloadType>();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareData, setShareData] = useState<ReturnType<typeof convertedTasks>[number]>();
  const [activeInternalUsers, setActiveInternalUsers] = useState<
    {
      value: string;
      name: string;
    }[]
  >([]);

  const [procedureListDivHeight, setProcedureListDivHeight] = useState(0);
  const [procedureListTableBodyHeight, setProcedureListTableBodyHeight] = useState(0);
  const [progress, setProgress] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [loading, setLoading] = useState(true);
  const [filterPayload, setFilterPayload] =
    useSetState<Omit<GetProcedureListNewPayloadType, "pageIndex" | "pageSize">>(
      getInitTablePayloadData()
    );
  const [tableSortData, setTableSortData] = useState<
    {
      sortColumn: string;
      isAscending: boolean;
    }[]
  >([]);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, string[] | number[] | null>>({});
  const [tableFilterDataNew, setTableFilterDataNew] =
    useState<GetProcedureListFilterNewResponseType>(getInitTableFilterData());
  const isFetchedFilterDataRef = useRef(false);

  const [procedureListData, setProcedureListData] = useState<
    GetProcedureListNewResponseType["newData"]
  >([]);
  const [pagenation, setPagination] = useSetState({
    pageIndex: 1,
    pageSize: 30,
    totalPage: 0,
    totalRecord: 0,
  });

  const [columnsVisible, setColumnsVisible] = useSetState(
    columnSelectOption.reduce((acc, item) => {
      acc[item.value] = columnSelectValue.includes(item.value);
      return acc;
    }, {} as ColumnBooleanMap)
  );

  const [topInputFilter, setTopInputFilter] = useSetState({
    taskLabelName: "",
    taskId: "",
  });

  const isOpex = handleUserRole(AppSetting.localStorageValue.role.get());

  const handleTopFilterClick = (
    filterOptionList: FilterOptionList,
    selectOptions: SelectOptions
  ) => {
    setTableFilterDataNew(getInitTableFilterData());
    isFetchedFilterDataRef.current = false;
    setTableSortData([]);
    setFilteredInfo({});
    setPagination({
      pageIndex: 1,
    });
    handleClearClick();

    let accountIds = selectOptions.accountAbbr ?? [];
    if (!isOpex && accountIds.length === 0) {
      accountIds = filterOptionList.accountAbbr.map((item) => item.value);
    }
    if (isOpex && accountIds.length === filterOptionList.accountAbbr.length) {
      accountIds = [];
    }
    let taskGroupIds = selectOptions.taskGroup ?? [];
    if (taskGroupIds.length === filterOptionList.taskGroup.length) {
      taskGroupIds = [];
    }
    let categoryIds = selectOptions.category ?? [];
    if (categoryIds.length === filterOptionList.category.length) {
      categoryIds = [];
    }

    const payload: GetProcedureListNewPayloadType = {
      ...getInitTablePayloadData(),

      pageIndex: 1,
      pageSize: pagenation.pageSize,

      accountIds,
      accountStatus: Number(selectOptions.accountStatus[0]),
      taskStatus: Number(selectOptions.taskStatus[0]),

      taskGroupIds: taskGroupIds.flatMap((item) => item.split(",").map(Number)),
      categoryIds: categoryIds.flatMap((item) => item.split(",").map(Number)),
    };

    setFilterPayload(payload);
    runGetProcedureList(payload);
  };

  const handleTableFilterSort = (
    tableFilterPara: {
      filterColumn: keyof SortItems;
      filterValue: string[];
    }[],
    tableSortPara: {
      sortColumn: string;
      isAscending: boolean;
    }[],
    filters: Record<TableFilterMapKeys, string[] | number[] | null>
  ) => {
    setPagination({
      pageIndex: 1,
    });
    setTableSortData(tableSortPara);
    setFilteredInfo(filters);

    const filterPart = getFilterResult(filters, tableFilterDataNew);
    if (filterPart === null) {
      setLoading(true);
      setTimeout(() => {
        setProcedureListData([]);
        setLoading(false);
      }, 1000);
      return;
    }

    const payload = {
      ...filterPayload,
      //order part
      orderByAsc: tableSortPara.length > 0 && tableSortPara[0].isAscending,
      orderBy: tableSortPara.length > 0 ? tableSortPara[0].sortColumn : null,
      //filter part
      ...filterPart,
      // pagenation part
      pageIndex: 1,
      pageSize: pagenation.pageSize,
    } as GetProcedureListNewPayloadType;
    setFilterPayload(payload);
    runGetProcedureList(payload);
  };

  const ShowSearchColumn = (payload: GetProcedureListNewPayloadType) => {
    let searchColumnArr = [];

    if (payload.taskId !== "") {
      searchColumnArr.push("taskIdShow");
    }

    if (payload.taskLabelName !== "") {
      searchColumnArr.push("taskLabelNameShow");
    }

    searchAndColumnSelectRef.current.handleColumnSearchSelect(searchColumnArr);
  };

  const handleClearClick = () => {
    setTopInputFilter({
      taskId: "",
      taskLabelName: "",
    });
  };

  const handleSearchClick = () => {
    setTableFilterDataNew(getInitTableFilterData());
    isFetchedFilterDataRef.current = false;
    setTableSortData([]);
    setFilteredInfo({});
    setPagination({
      pageIndex: 1,
    });
    const payload = {
      ...getInitTablePayloadData(),

      accountStatus: filterPayload.accountStatus,
      taskStatus: filterPayload.taskStatus,
      accountIds: filterPayload.accountIds,
      taskGroupIds: filterPayload.taskGroupIds,
      categoryIds: filterPayload.categoryIds,
      // pagenation part
      pageIndex: 1,
      pageSize: pagenation.pageSize,

      taskId: topInputFilter.taskId.trim(),
      taskLabelName: topInputFilter.taskLabelName.trim(),
    } as GetProcedureListNewPayloadType;

    setFilterPayload(payload);
    runGetProcedureList(payload);
    ShowSearchColumn(payload);
  };

  /**
   * function about click export button for procedure list
   */
  const handleExportClick = () => {
    runExportProcedureList(filterPayload);
  };

  const handleTaskLabelNameChange = (e: EventTarget & HTMLInputElement) => {
    setTopInputFilter({
      taskLabelName: e.value,
    });
  };

  const handleTaskIdChange = (e: EventTarget & HTMLInputElement) => {
    setTopInputFilter({
      taskId: e.value,
    });
  };

  const handlePageNumberChange = (pageIndex: number, pageSize: number) => {
    selectRef.current.blur();
    const newPageIndex = pageSize === pagenation.pageSize ? pageIndex : 1;
    setPagination({
      pageIndex: newPageIndex,
      pageSize,
    });
    const payload = {
      ...filterPayload,
      pageIndex: newPageIndex,
      pageSize,
    } as GetProcedureListNewPayloadType;
    setFilterPayload(payload);
    runGetProcedureList(payload);
  };

  const handleColumnSearchSelect = (valueArr: ColumnOptionKey[]) => {
    setColumnsVisible({
      ...valueArr.reduce((acc, item) => {
        acc[item] = true;
        return acc;
      }, {} as ColumnBooleanMap),
    });
  };

  const handleColumnSearchDeSelect = (value: ColumnOptionKey) => {
    setColumnsVisible({
      ...columnsVisible,
      [value]: false,
    });
  };

  /**
   * function about click confirm in assign ppo select options
   * @param {*} para submit api param
   * @param {*} submitSuccess submit success callback
   * @param {*} submitFaild submit error callback
   */
  const handleSubmitPPO = async (
    para: {
      primaryProcedureOwnerId: string;
      procedureTaskId: string;
    },
    submitSuccess: (res: any) => void,
    submitFaild: (error: string) => void
  ) => {
    const targetRecord = procedureListData.find((i) => i.procedureTaskId === para.procedureTaskId);
    if (!targetRecord) return;

    const result = await ProcedureListService.getTaskProcedurePPOInfo({
      procedureTaskId: targetRecord.procedureTaskId,
    }).catch(() => {
      console.log("Failed to get getTaskProcedurePPOInfo.");
    });

    if (!result) {
      submitFaild("Failed to get latest procedure data.");
      return;
    }

    if (
      !!result.primaryProcedureOwnerID !== !!targetRecord.primaryProcedureOwner ||
      result.procedureID !== targetRecord.procedureId ||
      !result.taskStatus
    ) {
      message.warning(
        "The PPO has been updated by another user. The latest data has been loaded to ensure accuracy.",
        4
      );

      if (!result.taskStatus) {
        setProcedureListData([]);
      }
      refreshProcedureList();
      submitSuccess(AssignPPOSelectStatus.Conflict);
      return;
    }
    ProcedureListService.assignPrimaryProcedureOwner(para, submitSuccess, () =>
      submitFaild("Assign Primary Procedure Owner Error!")
    );
  };

  const getActiveInternalUsersSuccess = (
    result: {
      userID: string;
      employeeFullName: string;
    }[]
  ) => {
    const usersArr: {
      value: string;
      name: string;
    }[] = [];
    result.forEach((item) => {
      const value = item.userID;
      const name = item.employeeFullName;
      usersArr.push({
        value,
        name,
      });
    });
    setActiveInternalUsers([...sortUsersList(usersArr)]);
    setLoading(false);
  };

  const getActiveInternalUsers = () => {
    ProcedureListService.getActiveInternalUserInfos(getActiveInternalUsersSuccess, () =>
      console.log("Get Procedure Document List Failed")
    );
  };

  const getProcedureDocumentListSuccess = (result: {
    procedureFiles: FileListItem[];
    supportingFiles: FileListItem[];
  }) => {
    const { procedureFiles, supportingFiles } = result;
    setProcedureFiles(procedureFiles);
    setSupportingFiles(supportingFiles);
    getActiveInternalUsers();
  };

  const getCoverSheetSuccess = async (result: GetCoverSheetResponseType) => {
    const blob = await generateCoverSheet(result);
    downloadPDF(blob, "Cover Sheet.pdf");
  };

  const getCoverSheet = () => {
    ProcedureListService.getCoverSheet(idsParam!, getCoverSheetSuccess, () =>
      console.log("Get Cover Sheet Failed")
    );
  };

  const getProcedureDocumentList = (para: GetCoverSheetPayloadType) => {
    ProcedureListService.getProcedureDocumentList(para, getProcedureDocumentListSuccess, () =>
      console.log("Get Procedure Document List Failed")
    );
  };

  const handleDownloadClick = (record: ReturnType<typeof convertedTasks>[number]) => {
    const { procedureId, procedureTaskId } = record;
    const para = {
      ProcedureId: procedureId,
      ProcedureTaskId: procedureTaskId,
    };
    setIdsParam(para);
    setLoading(true);
    getProcedureDocumentList(para);
    setIsDownloadModalOpen(true);
  };

  const handleDownloadSubmit = (fileList: FileListItem[]) => {
    let downloadList: { fileID: string; fileName: string }[] = [];
    fileList.forEach((data) => {
      downloadList.push({
        fileID: data.fileId,
        fileName: data.fileName,
      });
    });
    downloadFileByUrl(downloadList, () => {});
    getCoverSheet();
    setIsDownloadModalOpen(false);
  };

  const handleShareClick = (record: ReturnType<typeof convertedTasks>[number]) => {
    const { procedureId, procedureTaskId } = record;
    const para = {
      ProcedureId: procedureId,
      ProcedureTaskId: procedureTaskId,
    };
    setLoading(true);
    getProcedureDocumentList(para);
    setShareData(record);
    setIsShareModalOpen(true);
  };

  const handleDownloadCancel = () => {
    setIsDownloadModalOpen(false);
    setProcedureFiles([]);
    setSupportingFiles([]);
  };

  const handleShareCancel = () => {
    setIsShareModalOpen(false);
    setProcedureFiles([]);
    setSupportingFiles([]);
  };

  const {
    run: runGetProcedureList,
    loading: getProcedureListLoading,
    // re-fetch the list with the *last used* parameters
    refresh: refreshProcedureList,
  } = useRequest(ProcedureListService.getProcedureListNew, {
    manual: true,
    onBefore() {
      setLoading(true);
    },
    onSuccess: (res) => {
      setProcedureListData(res.newData);
      setPagination({
        totalPage: res.totalPage,
        totalRecord: res.totalRecord,
      });
    },
    onError: () => {
      message.error("Failed to get Procedure List data.");
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  const onFilterDropdownOpenChange = (flag: boolean) => {
    if (flag && !isFetchedFilterDataRef.current) {
      runGetProcedureListFilterNew({
        ...getInitTablePayloadData(),
        taskLabelName: filterPayload.taskLabelName,
        taskId: filterPayload.taskId,
        accountStatus: filterPayload.accountStatus,
        taskStatus: filterPayload.taskStatus,
        accountIds: filterPayload.accountIds,
        taskGroupIds: filterPayload.taskGroupIds,
        categoryIds: filterPayload.categoryIds,
      });
    }
  };

  const { run: runGetProcedureListFilterNew, loading: getProcedureListFilterNewLoading } =
    useRequest(ProcedureListService.getProcedureListFilterNew, {
      manual: true,
      onSuccess: (res) => {
        if (res instanceof Response) return;
        setTableFilterDataNew(res);
        isFetchedFilterDataRef.current = true;
      },
    });

  const { run: runExportProcedureList } = useRequest(ProcedureListService.exportProcedureListNew, {
    manual: true,
    onBefore: () => {
      getFakeProgress.reset();
      setExporting(true);
      setProgress(0);
      timer = setInterval(() => {
        const newProgress = getFakeProgress();
        setProgress(newProgress);
      }, 500);
    },
    onSuccess: (response) => {
      setProgress(100);
      clearInterval(timer!);
      const mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const byteCharacters = atob(response);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: mimeType });
      const date = getTimeZoneString(getDate(), "MMDDYYYY");
      downloadPDF(blob, `Procedure Master List - ${date}.xlsx`);
    },
    onError: () => {
      message.error("Failed to export Procedure List data.");
    },
    onFinally: () => {
      setTimeout(() => {
        setExporting(false);
        clearInterval(timer!);
      }, 1000);
    },
  });
  const calculateHeight = () => {
    const screenHeight = window.innerHeight;
    const desiredHeight = screenHeight - 114 + 8 + 10;
    setProcedureListDivHeight(desiredHeight);
    var procedureTopFilterDiv = document.getElementById("procedureTopFilter");
    var procedureTopFilterDivHeight = procedureTopFilterDiv!.offsetHeight;
    const tableContainer = document.querySelector(".ant-table-body");

    if (tableContainer) {
      const procedureListTableBodyHeight = desiredHeight - procedureTopFilterDivHeight - 191 + 8;
      setProcedureListTableBodyHeight(procedureListTableBodyHeight);
      (tableContainer as HTMLDivElement).style.height = `${procedureListTableBodyHeight}px`;
    }
  };

  useMount(() => {
    calculateHeight();
    window.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  });

  const columns = ProcedureListListColumn({
    tableFilterDataNew,
    filteredInfo,
    columnsVisible,
    handleSubmitPPO,
    setProcedureListData,
    procedureListData,
    tableSortData,
    handleDownloadClick,
    handleShareClick,
    filterLoading: getProcedureListFilterNewLoading,
    onFilterDropdownOpenChange,
    setLoading,
    refreshProcedureList,
  });

  const pageLoading = getProcedureListLoading || loading;

  return (
    <NormalSpin
      spinning={pageLoading}
      size="large"
      children={
        <div className={styles.procedureListDiv} style={{ height: procedureListDivHeight }}>
          <div className={styles.procedureTopFilter} id="procedureTopFilter">
            <TopFilter
              // ref={topFilterRef}
              handleFilterClick={handleTopFilterClick}
              isOpex={isOpex}
              handleClear={handleClearClick}
            />
            <SearchAndColumnSelect
              ref={searchAndColumnSelectRef}
              columnSelectValue={columnSelectValue}
              columnSelectOption={columnSelectOption}
              handleSearch={handleSearchClick}
              handleExport={handleExportClick}
              taskLabelName={topInputFilter.taskLabelName}
              taskId={topInputFilter.taskId}
              handleTaskLabelNameChange={handleTaskLabelNameChange}
              handleTaskIdChange={handleTaskIdChange}
              handleColumnSearchSelect={handleColumnSearchSelect}
              handleColumnSearchDeSelect={handleColumnSearchDeSelect}
              showExport={true}
              exportDisabled={exporting}
              progress={progress}
            />
          </div>
          <div className={styles.tableDiv}>
            <ListTable
              data={convertedTasks(procedureListData)}
              columns={columns}
              handleFilterSort={handleTableFilterSort}
              tableFilterMap={TableFilterMap}
              tableHeight={procedureListTableBodyHeight}
              testid="procedure-list-table"
              rowTestId={(record: any) => `procedure-list-row-${record.procedureId ?? record.id}`}
            />
            <div className={styles.procedurelistFooter}>
              <FooterPagination
                totalRecord={pagenation.totalRecord}
                totalPage={pagenation.totalPage}
                currentPage={{
                  pageNumber: pagenation.pageIndex,
                  pageSize: pagenation.pageSize,
                }}
                handlePageNumberChange={handlePageNumberChange}
                selectComponentClass={(props: any) => {
                  const options = props.children.map((item: ReactElement) => {
                    return {
                      // @ts-ignore
                      label: item.props.children,
                      // @ts-ignore
                      value: item.props.value,
                    };
                  });
                  return (
                    <Select {...props} options={options} optionLabelProp="label" ref={selectRef} />
                  );
                }}
                paginationTestId="procedure-list-pagination"
              />
            </div>
          </div>
          <DownloadDocument
            isModalOpen={isDownloadModalOpen}
            onSubmit={handleDownloadSubmit}
            onCancel={handleDownloadCancel}
            procedureFiles={procedureFiles}
            supportingFiles={supportingFiles}
          />
          <ShareDocument
            isModalOpen={isShareModalOpen}
            onCancel={handleShareCancel}
            procedureFiles={procedureFiles}
            supportingFiles={supportingFiles}
            shareData={shareData}
            activeInternalUsers={activeInternalUsers}
          />
        </div>
      }
    />
  );
}
